/**
 * Locked Application Form catalog — field keys, floors, and prebuilt
 * questions. Stored template JSON only stores on/off + custom questions.
 */
import { STUDENT_HOUSING_CREATE_ENABLED } from '#app/utils/property-types'

export type FormKind = 'standard' | 'student'

export function isEditableFormKind(kind: FormKind): boolean {
	return kind !== 'student' || STUDENT_HOUSING_CREATE_ENABLED
}

export type QuestionType = 'yes_no' | 'yes_no_explanation' | 'dropdown'

export type FieldDef = {
	key: string
	label: string
	/** Cannot be turned off while the section is enabled. */
	floor?: boolean
}

export type PrebuiltQuestionDef = {
	key: string
	prompt: string
	type: QuestionType
	options?: string[]
}

export type SectionCatalog = {
	fields?: readonly FieldDef[]
	prebuiltQuestions?: readonly PrebuiltQuestionDef[]
}

/** Same employer-contact chips for standard and student templates. */
export const EMPLOYER_CONTACT_FIELDS = [
	{ key: 'phone', label: 'Company Phone Number' },
	{ key: 'work_address', label: 'Work Address' },
] as const satisfies readonly FieldDef[]

export const STANDARD_FIELDS = {
	next_of_kin: [
		{ key: 'first_name', label: 'First Name', floor: true },
		{ key: 'last_name', label: 'Last Name', floor: true },
		{ key: 'relationship', label: 'Relationship', floor: true },
		{ key: 'email', label: 'Email', floor: true },
		{ key: 'phone', label: 'Phone Number', floor: true },
	],
	pet: [
		{ key: 'amount', label: 'Count', floor: true },
		{ key: 'type', label: 'Type', floor: true },
		{ key: 'breed', label: 'Breed' },
		{ key: 'weight', label: 'Weight' },
	],
	vehicle: [
		{ key: 'amount', label: 'Count', floor: true },
		{ key: 'type', label: 'Type', floor: true },
		{ key: 'license_plate', label: 'License Plate' },
		{ key: 'make_model', label: 'Make & Model' },
		{ key: 'color', label: 'Color' },
	],
	landlord_contact: [
		{ key: 'phone', label: 'Phone Number' },
		{ key: 'email', label: 'Email' },
	],
	employer_contact: EMPLOYER_CONTACT_FIELDS,
} as const satisfies Record<string, readonly FieldDef[]>

export const STUDENT_FIELDS = {
	occupant: [
		{ key: 'gender', label: 'Gender' },
		{ key: 'school', label: 'School' },
		{ key: 'field_of_study', label: 'Field Of Study' },
		{ key: 'level_of_study', label: 'Level Of Study' },
		{ key: 'expected_graduation', label: 'Expected Graduation' },
	],
	pet: [
		{ key: 'amount', label: 'Count', floor: true },
		{ key: 'type', label: 'Type', floor: true },
		{ key: 'weight', label: 'Weight' },
		{ key: 'breed', label: 'Breed' },
	],
	vehicle: [
		{ key: 'amount', label: 'Count', floor: true },
		{ key: 'type', label: 'Type', floor: true },
		{ key: 'license_plate', label: 'License Plate' },
		{ key: 'make_model', label: 'Make & Model' },
		{ key: 'color', label: 'Color' },
	],
	landlord_contact: [
		{ key: 'phone', label: 'Phone Number' },
		{ key: 'email', label: 'Email' },
	],
	employer_contact: EMPLOYER_CONTACT_FIELDS,
} as const satisfies Record<string, readonly FieldDef[]>

/** Standard forms have no locked catalog questions — only landlord-added ones. */
export const STANDARD_RENTAL_QUESTIONS: readonly PrebuiltQuestionDef[] = []

export const STANDARD_EMPLOYMENT_QUESTIONS: readonly PrebuiltQuestionDef[] = []

export const STUDENT_RENTAL_QUESTIONS: readonly PrebuiltQuestionDef[] = [
	{
		key: 'ever_evicted',
		prompt: 'Have you ever been evicted?',
		type: 'yes_no_explanation',
	},
	{
		key: 'felony_conviction',
		prompt:
			'Have you ever been convicted of a felony that involved an offense against property, persons, government officials, or that involved firearms, illegal drugs, or sex or sex crimes?',
		type: 'yes_no_explanation',
	},
	{
		key: 'do_you_smoke',
		prompt: 'Do you smoke?',
		type: 'yes_no',
	},
]

export const STUDENT_FINANCIAL_ASSISTANCE_OPTIONS = [
	'Scholarship',
	'Grant',
	'Loan',
	'Other',
] as const

export const STUDENT_EMPLOYMENT_QUESTIONS: readonly PrebuiltQuestionDef[] = [
	{
		key: 'government_assistance',
		prompt: 'Are you on government assistance?',
		type: 'yes_no',
	},
	{
		key: 'student_financial_assistance',
		prompt:
			'Are You Receiving Student Financial Assistance (Scholarships, Grants, Loans)?',
		type: 'dropdown',
		options: [...STUDENT_FINANCIAL_ASSISTANCE_OPTIONS],
	},
	{
		key: 'parental_assistance',
		prompt:
			'Are You Receiving Parental Assistance or Any Other Type of Income?',
		type: 'yes_no',
	},
]

export const SUGGESTED_STANDARD_DOCS = [
	{ id: 'doc-ref', name: 'Reference Letter' },
	{ id: 'doc-id', name: 'ID / License' },
	{ id: 'doc-pay', name: 'Paystubs' },
] as const

export const SUGGESTED_STUDENT_DOCS = [
	{ id: 'sdoc-ref', name: 'Reference Letter' },
	{ id: 'sdoc-bank', name: 'Bank slip' },
	{ id: 'sdoc-visa', name: 'Visitor visa' },
] as const

export function catalogFields(
	kind: FormKind,
	section: string,
): readonly FieldDef[] {
	if (kind === 'student') {
		return (STUDENT_FIELDS as Record<string, readonly FieldDef[]>)[section] ?? []
	}
	return (STANDARD_FIELDS as Record<string, readonly FieldDef[]>)[section] ?? []
}

export function catalogPrebuiltQuestions(
	kind: FormKind,
	section: 'rental_questions' | 'employment_questions',
): readonly PrebuiltQuestionDef[] {
	if (kind === 'student') {
		return section === 'rental_questions'
			? STUDENT_RENTAL_QUESTIONS
			: STUDENT_EMPLOYMENT_QUESTIONS
	}
	return section === 'rental_questions'
		? STANDARD_RENTAL_QUESTIONS
		: STANDARD_EMPLOYMENT_QUESTIONS
}

export function floorKeys(
	kind: FormKind,
	section: string,
): readonly string[] {
	return catalogFields(kind, section)
		.filter((field) => field.floor)
		.map((field) => field.key)
}
